import React, { useEffect, useMemo, useRef, useState } from "react";

import { Box, Tooltip, Typography, Skeleton } from "@mui/material";
import DataTable from "../../../Components/Common/datatable/DataTable";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import TrafficLight from "./TrafficLight";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoPopover from "../../../Components/Common/poper/InfoPopover";
import { DateFormatterDDMMMYYYY } from "../../../Components/Common/DateFormatter";
import { triggerRefresh } from "../../../redux/refresh";
import { useSupport } from "../../../context/SupportContext";

function CurrentForecastTracker() {
  const [filteredData, setFilteredData] = useState([]);
  let { card1CombinationDetails, setCard1CombinationDetails } = useSupport();
  const [loading, setLoading] = useState(true); 

  const gridRef = useRef();

  const finalTreeData = useSelector((state) => state.entity.finalTreeData);
  const roles = useSelector((state) => state.user.roles);

  const dispatch = useDispatch();
  const refreshKey = useSelector((state) => state.refresh.refreshKey);

  useEffect(() => {
    const loadCurrentForecastData = async () => {
      setLoading(true);
      try {
        const response = await ApiService.post(
          "/v1/forecast-tracker/get-applicable-forecast-status",
          finalTreeData
        );
        setFilteredData(response.data);
      } catch (error) {
        console.error("Error loading current forecast data", error);
      } finally {
        setLoading(false);
      }
    };

    if (finalTreeData.length > 0) {
      loadCurrentForecastData();
    } else {
      setFilteredData([]);
      setLoading(false);
    }
  }, [refreshKey, finalTreeData]);

  function dateComparator(date1, date2) {
    const date1Number = _monthToNum(date1);
    const date2Number = _monthToNum(date2);

    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }

    return date1Number - date2Number;
  }

  function _monthToNum(date) {
    if (date === undefined || date === null || date.length !== 10) {
      return null;
    }

    var yearNumber = parseInt(date.substring(6, 10), 10);
    var monthNumber = parseInt(date.substring(3, 5), 10);
    var dayNumber = parseInt(date.substring(0, 2), 10);

    var result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
    return result;
  }

  const StatusCellRenderer = (params) => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <TrafficLight status={params.value} />
        <span>{params.value}</span>
      </div>
    );
  };

  useEffect(() => {
    if (finalTreeData.length > 0) {
      const postData = async () => {
        try {
          setLoading(true); // Start loading
          const response = await ApiService.post(
            "/v1/forecast-tracker/get-applicable-forecast-status",
            finalTreeData
          );
          setFilteredData(response.data);
        } catch (error) {
          console.error("Error posting data", error);
        } finally {
          setLoading(false); // Stop loading
        }
      };
      postData();
    } else {
      setFilteredData([]);
      setLoading(false); // Stop loading if no data
    }
  }, [finalTreeData]);

  const commonColumns = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      field: "supportCheckbox",
      headerName: "",
      sortable: false,
      width: 50,
      suppressSizeToFit: true,
    },
    {
      field: "bottler",
      headerName: "Bottler",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 190,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.bottler}`} arrow>
          <div>{params.data.bottler}</div>
        </Tooltip>
      ),
    },
    {
      field: "customerName",
      headerName: "Forecast Partner",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 270,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.customerName}`} arrow>
          <div>{params.data.customerName}</div>
        </Tooltip>
      ),
    },
    {
      field: "locationName",
      headerName: "Plant",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 150,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.locationName}`} arrow>
          <div>{params.data.locationName}</div>
        </Tooltip>
      ),
    },
    {
      field: "fiscalmonth",
      headerName: "Fiscal Period",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 220,
      cellRenderer: (params) => <div>{params.data.fiscalmonth}</div>,
    },
    {
      field: "forecastType",
      headerName: "Forecast Term",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 230,
    },
    {
      field: "pendingBy",
      headerName: "Forecast Deadline",
      sortable: true,
      sort: "desc",
      suppressHeaderMenuButton: true,
      SortIcon: true,
      filter: "agDateColumnFilter",
      filterParams: {
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
      },
      width: 270,
      valueFormatter: (params) => {
        return DateFormatterDDMMMYYYY(params.value);
      },
      comparator: dateComparator,
    },
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      width: 210,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      cellRenderer: StatusCellRenderer,
      resizable: true,
    },
  ]);

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const getColumnDefinition = () => {
    let columns = [...commonColumns];
    // if (roles.includes('Bottler')) {
    //   columns.push(uploadColumn);
    // }

    // Adjust widths dynamically
    const totalWidth = columns.reduce((sum, col) => sum + col.width, 0);
    const availableWidth = 1100; // Adjust based on your container width
    const scaleFactor = availableWidth / totalWidth;

    return columns.map((col) => ({
      ...col,
      width: Math.floor(col.width * scaleFactor),
    }));
  };

  const handleSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const combinationDetails = selectedRows.map(({ bottler, locationName, customerName }) => ({
      bottler,
      locationName,
      customerName,
    }));

    setCard1CombinationDetails(combinationDetails);
  };

  const columnDefinition = useMemo(getColumnDefinition, [roles, commonColumns]);

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();

      // Adding a resize event listener to handle zoom changes
      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  return (
    <React.Fragment>
      <div class="container">
        {/* Tabs */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Table Header */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" align="left" gutterBottom="true">
                Concentrate Forecast Upload Status
              </Typography>
              <InfoPopover margin="0 0 3px 0" />
            </Box>

            <Tooltip title="Refresh" arrow placement="right">
              <RefreshIcon
                // onClick={async () => {
                //   try {
                //     setLoading(true); // Show loading indicator
                //     const response = await ApiService.post(
                //       "/v1/forecast-tracker/get-applicable-forecast-status",
                //       finalTreeData
                //     );
                //     setFilteredData(response.data); // Update the table data
                //   } catch (error) {
                //     console.error("Error refreshing data", error);
                //   } finally {
                //     setLoading(false); // Hide loading indicator
                //   }
                // }}

                sx={{
                  cursor: "pointer",
                  color: "grey",
                  "&:hover": {
                    color: "grey",
                  },
                }}
                onClick={() => {
                  dispatch(triggerRefresh());
                }}
              />
            </Tooltip>
          </Box>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={280}
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : (
            <DataTable
              ref={gridRef}
              columns={columnDefinition}
              rows={filteredData || []}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              //paginationAutoPageSize={true}
              suppressPaginationPanel={false}
              tableHeight={300}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              onGridReady={onGrid}
              suppressHeaderContextMenu={true}
              onSelectionChanged={handleSelectionChanged}
            />
          )}
        </Box>
      </div>
    </React.Fragment>
  );
}

export default CurrentForecastTracker;
